import React, { useState } from 'react'
import { CaptionedImage } from '../lib/sanity-types'
import styled from '@emotion/styled'
import GatsbyImage from 'gatsby-image/withIEPolyfill'
import { getFluidGatsbyImage } from 'gatsby-source-sanity'
import * as sanity from '../lib/sanity'
import {
  useTransition,
  animated,
  TransparentButton
} from '@sitewerk/juraholzbau-design-system'
import { CircleArrowLeft, CircleArrowRight } from './icons'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  > * + * {
    margin-top: 10px;
  }
`

const Slides = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 400px;
  height: 30vh;
  width: 100%;
`

const Slide = styled(animated.div)`
  cursor: pointer;
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  transition: 0.2s ease filter;

  &:hover {
    filter: brightness(80%);
  }
`

export const ContentSlider: React.FC<{ images: CaptionedImage[] }> = ({
  images
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const transitions = useTransition(index, p => p, {
    initial: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    from: { opacity: 0, transform: 'translate3d(100%,0,0)' },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: { opacity: 0, transform: 'translate3d(-50%,0,0)' }
  })

  const imageUrls = images.map(image => image.asset.url)

  const prev = () => setIndex(i => (i > 0 ? i - 1 : images.length - 1))
  const next = () => setIndex(i => (i + 1) % images.length)

  return (
    <Wrapper data-component="slider">
      <Slides>
        {transitions.map(({ item, props, key }) => {
          const image = images[item]

          if (!image) return null

          return (
            <Slide
              style={props}
              key={key}
              onClick={() => setIsLightboxOpen(true)}
            >
              <GatsbyImage
                style={{ height: '100%' }}
                objectFit="contain"
                fluid={getFluidGatsbyImage(
                  image.asset,
                  { maxWidth: 1920 },
                  sanity.config
                )}
              />
            </Slide>
          )
        })}
      </Slides>
      <div>
        <TransparentButton onClick={prev}>
          <CircleArrowLeft />
        </TransparentButton>
        <TransparentButton onClick={next}>
          <CircleArrowRight />
        </TransparentButton>
      </div>

      {isLightboxOpen && (
        <Lightbox
          mainSrc={imageUrls[index]}
          nextSrc={imageUrls[(index + 1) % imageUrls.length]}
          prevSrc={imageUrls[(index + imageUrls.length - 1) % imageUrls.length]}
          onCloseRequest={() => setIsLightboxOpen(false)}
          onMovePrevRequest={() =>
            setIndex((index + imageUrls.length - 1) % imageUrls.length)
          }
          onMoveNextRequest={() => setIndex((index + 1) % imageUrls.length)}
        />
      )}
    </Wrapper>
  )
}
